<template>
  <div class="message-center">
    <!-- 消息图标 -->
    <div class="message-icon-wrapper" @click="toggleDropdown">
      <el-badge :value="unreadCount" :hidden="unreadCount === 0" :max="99">
        <i class="el-icon-bell message-icon"
          :style="{ color: iconColor }"></i>
      </el-badge>
    </div>

    <!-- 下拉消息列表 -->
    <div v-show="dropdownVisible" class="message-dropdown" v-clickoutside="closeDropdown">
      <div class="dropdown-header">
        <span class="dropdown-title">未读消息</span>
        <span v-if="unreadMessages.length > 0" class="mark-all-read" @click="handleMarkAllRead">一键已读</span>
      </div>
      <div class="dropdown-body" v-loading="dropdownLoading">
        <div v-if="unreadMessages.length === 0" class="empty-message">暂无未读消息</div>
        <div v-for="msg in unreadMessages.slice(0, 5)" :key="msg.id" class="message-item unread"
          @click="handleViewDetail(msg)">
          <div class="message-dot"></div>
          <div class="message-content">
            <div class="message-text" :title="msg.content">{{ msg.content }}</div>
            <div class="message-time">{{ msg.createTime }}</div>
          </div>
        </div>
        <div v-if="unreadMessages.length > 5" class="more-tip">还有 {{ unreadMessages.length - 5 }} 条未读消息</div>
      </div>
      <div class="dropdown-footer" @click="openMessageDialog">
        <span>查看全部</span>
        <i class="el-icon-arrow-right"></i>
      </div>
    </div>

    <!-- 消息中心弹窗 -->
    <el-dialog title="消息中心" :visible.sync="dialogVisible" width="700px" :close-on-click-modal="false"
      :append-to-body="true" @close="handleDialogClose" top="8vh">
      <div class="dialog-header-bar">
        <el-radio-group v-model="messageFilter" size="small" @change="handleFilterChange">
          <el-radio-button label="unread">未读消息</el-radio-button>
          <el-radio-button label="all">全部消息</el-radio-button>
        </el-radio-group>
        <el-button v-if="filteredMessages.length > 0 && messageFilter === 'unread'" type="text" size="small"
          @click="handleMarkAllRead">
          一键已读
        </el-button>
      </div>
      <div class="dialog-body" v-loading="dialogLoading">
        <div v-if="filteredMessages.length === 0" class="empty-message-dialog">
          <i class="el-icon-message"></i>
          <p>{{ messageFilter === 'unread' ? '暂无未读消息' : '暂无消息' }}</p>
        </div>
        <div v-for="msg in filteredMessages" :key="msg.id" class="dialog-message-item"
          :class="{ 'is-read': msg.readStatus }">
          <div class="msg-main">
            <div class="msg-content-wrapper">
              <div class="msg-badge" v-if="!msg.readStatus"></div>
              <div class="msg-text" :title="msg.content">{{ msg.content }}</div>
            </div>
            <div class="msg-meta">
              <span class="msg-time">{{ msg.createTime }}</span>
              <el-button type="text" size="mini" class="msg-detail-btn" @click="handleViewDetail(msg)">
                详情
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
const POLL_INTERVAL = 5000

// 消息类型说明（用于日志或扩展）
const MESSAGE_TYPE_MAP = {
  '15': '证书回收消息'
}

export default {
  name: 'MessageCenter',
  props: {
    iconColor: {
      type: String,
      default: '#2c3441'
    }
  },
  data() {
    return {
      dropdownVisible: false,
      dialogVisible: false,
      dropdownLoading: false,
      dialogLoading: false,
      messageFilter: 'unread',
      unreadMessages: [],
      allMessages: [],
      pollTimer: null
    }
  },
  computed: {
    unreadCount() {
      return this.unreadMessages.length
    },
    filteredMessages() {
      if (this.messageFilter === 'unread') {
        return this.allMessages.filter(m => !m.readStatus)
      }
      return this.allMessages
    }
  },
  directives: {
    clickoutside: {
      bind(el, binding) {
        function documentHandler(e) {
          if (el.contains(e.target)) return
          binding.value(e)
        }
        el._clickoutside = documentHandler
        document.addEventListener('click', documentHandler)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickoutside)
        delete el._clickoutside
      }
    }
  },
  mounted() {
    this.fetchUnreadMessages()
    this.startPoll()
  },
  beforeDestroy() {
    this.stopPoll()
  },
  methods: {
    // 获取未读消息
    fetchUnreadMessages() {
      this.axios.get(this.$url.messagePage, {
        params: { current: 1, size: 999, readStatus: false }
      }).then(res => {
        if (res.code === '200' || res.code === 200) {
          this.unreadMessages = (res.data?.records || []).map(item => ({
            ...item,
            readStatus: !!item.readStatus
          }))
        }
      }).catch(() => {
        // 静默失败，不影响用户体验
      })
    },
    // 获取全部消息
    fetchAllMessages() {
      this.dialogLoading = true
      this.axios.get(this.$url.messagePage, {
        params: { current: 1, size: 999, readStatus: null }
      }).then(res => {
        if (res.code === '200' || res.code === 200) {
          this.allMessages = (res.data?.records || []).map(item => ({
            ...item,
            readStatus: !!item.readStatus
          }))
        }
      }).catch(() => {
        this.$message.error('获取消息列表失败')
      }).finally(() => {
        this.dialogLoading = false
      })
    },
    // 标记已读
    markRead(ids) {
      if (!ids || ids.length === 0) return Promise.resolve()
      return this.axios.put(this.$url.messageUpdateReadStatus, { ids }).then(res => {
        if (res.code === '200' || res.code === 200) {
          return true
        }
        return false
      }).catch(() => {
        return false
      })
    },
    // 开始轮询
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => {
        this.fetchUnreadMessages()
      }, POLL_INTERVAL)
    },
    // 停止轮询
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    // 切换下拉显示
    toggleDropdown(e) {
      e.stopPropagation()
      this.dropdownVisible = !this.dropdownVisible
      if (this.dropdownVisible) {
        this.fetchUnreadMessages()
      }
    },
    // 关闭下拉
    closeDropdown() {
      this.dropdownVisible = false
    },
    // 打开消息中心弹窗
    openMessageDialog() {
      this.dropdownVisible = false
      this.dialogVisible = true
      this.messageFilter = 'unread'
      this.fetchAllMessages()
    },
    // 弹窗关闭
    handleDialogClose() {
      this.messageFilter = 'unread'
    },
    // 筛选切换
    handleFilterChange() {
      // 筛选本地数据即可，不需要重新请求
    },
    // 查看详情
    handleViewDetail(msg) {
      if (!msg.readStatus) {
        this.markRead([msg.id]).then(success => {
          if (success) {
            msg.readStatus = true
            // 从未读列表中移除
            const idx = this.unreadMessages.findIndex(m => m.id === msg.id)
            if (idx > -1) {
              this.unreadMessages.splice(idx, 1)
            }
            // 更新全部消息列表中的状态
            const allIdx = this.allMessages.findIndex(m => m.id === msg.id)
            if (allIdx > -1) {
              this.$set(this.allMessages[allIdx], 'readStatus', true)
            }
          }
          this.navigateByMessage(msg)
        })
      } else {
        this.navigateByMessage(msg)
      }
      this.dropdownVisible = false
    },
    // 从消息内容中提取订单编号
    extractOrderNum(content) {
      if (!content) return null
      const match = content.match(/【订单编号[：:](\w+)】/)
      return match ? match[1] : null
    },
    // 根据消息类型跳转
    navigateByMessage(msg) {
      // 关闭弹窗
      this.dialogVisible = false
      const type = String(msg.type)
      const targetPath = '/order'
      const isOnOrderPage = this.$route.path === targetPath

      // type 15: 证书回收消息，提取订单编号后搜索定位
      if (type === '15') {
        const orderNum = this.extractOrderNum(msg.content)
        if (orderNum) {
          sessionStorage.setItem('messageTargetOrderNum', orderNum)
          if (isOnOrderPage) {
            window.location.reload()
          } else {
            this.$router.push(targetPath)
          }
          return
        }
      }

      if (isOnOrderPage) {
        window.location.reload()
      } else {
        this.$router.push(targetPath)
      }
    },
    // 一键已读
    handleMarkAllRead() {
      const ids = this.unreadMessages.map(m => m.id)
      if (ids.length === 0) {
        this.$message.info('没有未读消息')
        return
      }
      this.markRead(ids).then(success => {
        if (success) {
          this.$message.success('已全部标记为已读')
          // 更新本地状态
          this.unreadMessages = []
          this.allMessages.forEach(m => {
            this.$set(m, 'readStatus', true)
          })
          // 如果在弹窗中，刷新全部消息
          if (this.dialogVisible) {
            this.fetchAllMessages()
          }
        } else {
          this.$message.error('标记已读失败')
        }
      })
    }
  }
}
</script>

<style scoped>
.message-center {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.message-icon-wrapper {
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-icon {
  font-size: 20px;
  transition: color 0.2s;
}

.message-icon-wrapper:hover .message-icon {
  color: #004a8c !important;
}

/* 下拉面板 */
.message-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: -20px;
  width: 360px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  z-index: 2000;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-title {
  font-size: 15px;
  font-weight: 600;
  color: #2c3441;
}

.mark-all-read {
  font-size: 13px;
  color: #004a8c;
  cursor: pointer;
}

.mark-all-read:hover {
  color: #006bb8;
}

.dropdown-body {
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
}

.empty-message {
  text-align: center;
  padding: 40px 16px;
  color: #999;
  font-size: 14px;
}

.message-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f5f5f5;
}

.message-item:last-child {
  border-bottom: none;
}

.message-item:hover {
  background: #f5f9ff;
}

.message-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f56c6c;
  margin-top: 6px;
  margin-right: 10px;
  flex-shrink: 0;
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message-text {
  font-size: 13px;
  color: #2c3441;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
}

.message-time {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.more-tip {
  text-align: center;
  padding: 8px;
  font-size: 12px;
  color: #999;
}

.dropdown-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-top: 1px solid #f0f0f0;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.dropdown-footer:hover {
  background: #f5f9ff;
  color: #004a8c;
}

.dropdown-footer i {
  margin-left: 4px;
  font-size: 12px;
}

/* 弹窗样式 */
.dialog-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.dialog-body {
  max-height: 480px;
  overflow-y: auto;
}

.empty-message-dialog {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-message-dialog i {
  font-size: 48px;
  color: #ddd;
  margin-bottom: 12px;
  display: block;
}

.empty-message-dialog p {
  font-size: 14px;
}

.dialog-message-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}

.dialog-message-item:last-child {
  border-bottom: none;
}

.dialog-message-item:hover {
  background: #fafafa;
}

.dialog-message-item.is-read {
  opacity: 0.75;
}

.msg-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.msg-content-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.msg-badge {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f56c6c;
  margin-top: 5px;
  flex-shrink: 0;
}

.msg-text {
  font-size: 14px;
  color: #2c3441;
  line-height: 1.5;
  word-break: break-all;
}

.msg-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 16px;
}

.msg-time {
  font-size: 12px;
  color: #999;
}

.msg-detail-btn {
  color: #004a8c;
  padding: 0;
  font-size: 13px;
}

.msg-detail-btn:hover {
  color: #006bb8;
}

/* 覆盖 element badge 样式 */
::v-deep .el-badge__content {
  border: none;
  font-size: 11px;
  height: 16px;
  line-height: 16px;
  padding: 0 5px;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .message-dropdown {
    width: 300px;
    right: -60px;
  }
}
</style>
